import { GET_COMPANY } from "@apiUtils";
import { mutationFetch } from "@atlas/sdk";
import type {
  ICreateIdpGroupRequest,
  IIdpGroup,
  IIdpGroupsCreateResponse,
  IIdpGroupsListResponse,
  IUpdateIdpGroupRequest,
} from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const IDP_GROUPS_KEY = "idpGroupsKey";

export const getIdpGroupsKey = (
  companyUuid: string,
  basePath?: string,
  includeInactive = true,
) => [IDP_GROUPS_KEY, companyUuid, basePath, includeInactive] as const;

const buildCompanyUrl = (
  companyUuid: string,
  suffix: string,
  basePath?: string,
) => {
  const path = `${GET_COMPANY}/${companyUuid}${suffix}`;
  return basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
};

async function listIdpGroupsByStatus(
  companyUuid: string,
  isActive: boolean,
  basePath?: string,
): Promise<IIdpGroup[]> {
  const url = `${buildCompanyUrl(companyUuid, "/groups", basePath)}?isActive=${isActive}`;
  const data = await mutationFetch<IIdpGroupsListResponse>({
    url,
    method: "GET",
  });
  const groups = Array.isArray(data.groups) ? data.groups : [];
  return groups.map((group) => ({
    ...group,
    isActive: group.isActive ?? isActive,
  }));
}

export type UseIdpGroupsOptions = Partial<
  UseQueryOptions<
    IIdpGroup[],
    AxiosError,
    IIdpGroup[],
    ReturnType<typeof getIdpGroupsKey>
  >
> & {
  /** When false, only `GET .../groups?isActive=true` (OpenAPI default). */
  includeInactive?: boolean;
};

const invalidateIdpGroupsQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  companyUuid: string,
) => {
  queryClient.invalidateQueries({
    queryKey: [IDP_GROUPS_KEY, companyUuid],
  });
};

export const useIdpGroups = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: UseIdpGroupsOptions,
) => {
  const { includeInactive = true, ...queryOptions } = options ?? {};
  return useQuery({
    queryKey: getIdpGroupsKey(companyUuid ?? "", basePath, includeInactive),
    queryFn: async () => {
      const active = await listIdpGroupsByStatus(
        companyUuid ?? "",
        true,
        basePath,
      );
      if (!includeInactive) {
        return active;
      }
      const inactive = await listIdpGroupsByStatus(
        companyUuid ?? "",
        false,
        basePath,
      ).catch(() => [] as IIdpGroup[]);
      const byId = new Map<string, IIdpGroup>();
      [...active, ...inactive].forEach((group) => {
        byId.set(group.id, group);
      });
      return Array.from(byId.values());
    },
    enabled: !!companyUuid,
    ...queryOptions,
  });
};

export const useCreateIdpGroup = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: UseMutationOptions<IIdpGroup, AxiosError, ICreateIdpGroupRequest>,
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};
  return useMutation({
    ...mutationOptions,
    mutationFn: async (group: ICreateIdpGroupRequest) => {
      const url = buildCompanyUrl(companyUuid ?? "", "/groups", basePath);
      const data = await mutationFetch<IIdpGroupsCreateResponse>({
        url,
        method: "POST",
        body: { groups: [{ ...group, isActive: group.isActive ?? true }] },
      });
      return data.groups[0];
    },
    onSuccess: (...args) => {
      if (companyUuid) {
        invalidateIdpGroupsQueries(queryClient, companyUuid);
      }
      onSuccess?.(...args);
    },
  });
};

export const useUpdateIdpGroup = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: UseMutationOptions<
    IIdpGroup,
    AxiosError,
    { groupId: string; body: IUpdateIdpGroupRequest }
  >,
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};
  return useMutation({
    ...mutationOptions,
    mutationFn: async ({ groupId, body }) => {
      const url = buildCompanyUrl(
        companyUuid ?? "",
        `/groups/${encodeURIComponent(groupId)}`,
        basePath,
      );
      return mutationFetch<IIdpGroup>({
        url,
        method: "PUT",
        body: body,
      });
    },
    onSuccess: (...args) => {
      if (companyUuid) {
        invalidateIdpGroupsQueries(queryClient, companyUuid);
      }
      onSuccess?.(...args);
    },
  });
};

export const useDeleteIdpGroup = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: UseMutationOptions<void, AxiosError, string>,
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};
  return useMutation({
    ...mutationOptions,
    mutationFn: async (groupId: string) => {
      const url = buildCompanyUrl(
        companyUuid ?? "",
        `/groups/${encodeURIComponent(groupId)}`,
        basePath,
      );
      await mutationFetch<void>({
        url,
        method: "DELETE",
      });
    },
    onSuccess: (...args) => {
      if (companyUuid) {
        invalidateIdpGroupsQueries(queryClient, companyUuid);
      }
      onSuccess?.(...args);
    },
  });
};
